/**
 * ReactiveEdgeService
 *
 * E018-S009: Wrapper service that integrates EdgeOrchestrator into
 * the @toeverything/infra service system.
 *
 * This service replaces EdgeIntegrationService with the new reactive
 * architecture that eliminates race conditions.
 *
 * Key differences from EdgeIntegrationService:
 * - Uses reactive data flow instead of imperative scanning
 * - Lazy path calculation at render time
 * - BlockRegistry guarantees block availability
 * - Reconciliation pattern for ghost/edge sync
 */

import { DebugLogger } from '@affine/debug';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type { BlockStdScope } from '@blocksuite/std';
import type { GfxController } from '@blocksuite/std/gfx';
import { Service } from '@toeverything/infra';

import type { DocService } from '../../../doc';
import type { EditorService } from '../../../editor';
import type { AutomationDiscoveryService } from '../../services/automation-discovery';
import type { DatabaseDiscoveryService } from '../../services/database-discovery';
import { parseXywhToPosition } from '../core/constants';
import type { WorkspaceDatabaseInfo } from '../core/interfaces/IGhostDerivationService';
import { EdgeOrchestrator } from '../orchestration/EdgeOrchestrator';
import { EdgeRenderingServiceAdapter } from '../orchestration/EdgeRenderingServiceAdapter';
import {
  applyCanvasGridStyle,
  loadEdgeSettings,
} from '../utils/config-persistence';
import type { EdgeSidebarSetup } from './edge-sidebar-setup';

// =============================================================================
// Debug Configuration
// =============================================================================

// Dev-only diagnostics. Off by default; enable via `?debug` (DebugLogger /
// @affine/debug). Prod builds strip console.* via webpack pure_funcs anyway.
const logger = new DebugLogger('affine:streamlain:edge');
const GFX_RETRY_DELAY_MS = 250;
const GFX_RETRY_MAX_ATTEMPTS = 120;

// =============================================================================
// Service
// =============================================================================

type BlockStdLike = Pick<BlockStdScope, 'get' | 'getOptional'>;

type EditorContainerLike = {
  host?: {
    doc?: { blockCollection?: { doc?: { id?: string } } };
    querySelector?: (selector: string) => Element | null;
    std?: BlockStdLike;
  } | null;
  mode?: string;
  querySelector?: (selector: string) => Element | null;
  std?: BlockStdLike;
};

type EdgelessRootLike = Element & {
  host?: { std?: BlockStdLike };
  std?: BlockStdLike;
};

/**
 * ReactiveEdgeService
 *
 * Integrates the reactive EdgeOrchestrator into the service system.
 */
export class ReactiveEdgeService extends Service {
  private orchestrator: EdgeOrchestrator | null = null;
  private currentGfx: GfxController | null = null;
  private activeEditorContainer: EditorContainerLike | null = null;
  private unsubscribe: (() => void) | null = null;
  private gfxRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private editorContainerGeneration = 0;

  constructor(
    private readonly editorService: EditorService,
    private readonly edgeSidebarSetup: EdgeSidebarSetup,
    private readonly databaseDiscovery: DatabaseDiscoveryService,
    private readonly automationDiscovery: AutomationDiscoveryService,
    private readonly docService: DocService
  ) {
    super();
    this.subscribeToEditorContainer();
  }

  /**
   * Subscribes to editor container changes.
   * NOTE: Don't check if editor exists - just subscribe directly (like EdgeIntegrationService)
   */
  private subscribeToEditorContainer(): void {
    const editor = this.editorService.editor;

    // Subscribe to editorContainer$ - same pattern as EdgeIntegrationService
    const subscription = editor.editorContainer$.subscribe(editorContainer => {
      if (editorContainer) {
        this.activeEditorContainer = editorContainer;
        this.editorContainerGeneration++;
        this.attemptInitializeFromEditorContainer(
          editorContainer,
          this.editorContainerGeneration,
          0
        );
      } else {
        this.clearGfxRetry();
        this.activeEditorContainer = null;
        this.editorContainerGeneration++;
        this.disposeOrchestrator();
      }
    });

    this.unsubscribe = () => subscription.unsubscribe();
  }

  /**
   * Initializes the EdgeOrchestrator.
   */
  private initializeOrchestrator(
    gfx: GfxController,
    editorContainer: EditorContainerLike,
    attempt = 0
  ): void {
    // Don't re-initialize an active orchestrator for the same gfx. If the
    // previous attempt only marked currentGfx while waiting for DOM/doc ready,
    // continue initialization; Firefox commonly hits that reload timing.
    if (this.currentGfx === gfx && this.orchestrator) return;

    if (this.currentGfx !== gfx) {
      // Dispose previous orchestrator
      this.disposeOrchestrator();

      // Mark as initializing for this gfx (BEFORE container check, like EdgeIntegrationService)
      this.currentGfx = gfx;
    }

    // Find canvas container (same approach as EdgeIntegrationService)
    const canvasContainer = this.resolveCanvasContainer(editorContainer);

    if (!canvasContainer) {
      this.scheduleInitializeRetry(
        gfx,
        editorContainer,
        attempt,
        '[ReactiveEdgeService] Canvas container unavailable after retry timeout'
      );
      return;
    }

    // Get store and rootId (same approach as EdgeIntegrationService)
    const store = gfx.doc;
    const rootId = gfx.doc.root?.id;

    if (!store || !rootId) {
      this.scheduleInitializeRetry(
        gfx,
        editorContainer,
        attempt,
        '[ReactiveEdgeService] Store/rootId unavailable after retry timeout'
      );
      return;
    }

    // Get current doc ID for filtering incoming automations
    const currentDocId = store.id ?? '';

    // Create orchestrator
    this.orchestrator = new EdgeOrchestrator({
      gfx,
      store: store as import('@blocksuite/store').Store,
      rootId,
      container: canvasContainer as HTMLElement,
      currentDocId,
      waitForInitialSync: () => this.docService.doc.waitForSyncReady(),
      getWorkspaceDatabases: () => this.getWorkspaceDatabases(currentDocId),
      onWorkspaceDatabasesChange: callback => {
        const databaseSubscription =
          this.databaseDiscovery.databases$.subscribe(() => {
            callback();
          });
        const automationSubscription =
          this.automationDiscovery.blocks$.subscribe(() => {
            callback();
          });
        const accessibleAutomationSubscription =
          this.automationDiscovery.accessibleAutomations$?.subscribe(() => {
            callback();
          });

        return () => {
          databaseSubscription.unsubscribe();
          automationSubscription.unsubscribe();
          accessibleAutomationSubscription?.unsubscribe();
        };
      },
      // Find automations from other docs that reference a given database
      findIncomingAutomations: targetDatabaseId => {
        return this.automationDiscovery.findIncomingAutomations(
          targetDatabaseId,
          currentDocId
        );
      },
      // Find TRASHED automations in the SAME doc that reference a given database
      findTrashedLocalAutomations: targetDatabaseId => {
        return this.automationDiscovery.findTrashedLocalAutomations(
          targetDatabaseId,
          currentDocId
        );
      },
    });

    // Initialize
    this.orchestrator.initialize();
    this.currentGfx = gfx;

    // Connect sidebar to EdgeRenderingService
    this.connectSidebar();

    logger.debug('[ReactiveEdgeService] Orchestrator initialized');
  }

  private scheduleInitializeRetry(
    gfx: GfxController,
    editorContainer: EditorContainerLike,
    attempt: number,
    timeoutWarning: string
  ): void {
    if (attempt >= GFX_RETRY_MAX_ATTEMPTS) {
      console.warn(timeoutWarning);
      return;
    }

    this.clearGfxRetry();
    this.gfxRetryTimer = setTimeout(() => {
      this.gfxRetryTimer = null;
      if (this.activeEditorContainer !== editorContainer) return;
      if (this.currentGfx !== gfx || this.orchestrator) return;
      this.initializeOrchestrator(gfx, editorContainer, attempt + 1);
    }, GFX_RETRY_DELAY_MS);
  }

  private attemptInitializeFromEditorContainer(
    editorContainer: EditorContainerLike,
    generation: number,
    attempt: number
  ): void {
    if (generation !== this.editorContainerGeneration) return;
    if (this.orchestrator) return;

    const gfx = this.resolveGfxController(editorContainer);

    if (gfx) {
      this.clearGfxRetry();
      this.initializeOrchestrator(gfx, editorContainer);
      return;
    }

    if (attempt >= GFX_RETRY_MAX_ATTEMPTS) {
      console.warn(
        '[ReactiveEdgeService] GfxController unavailable after retry timeout'
      );
      return;
    }

    this.clearGfxRetry();
    this.gfxRetryTimer = setTimeout(() => {
      this.gfxRetryTimer = null;
      if (this.activeEditorContainer !== editorContainer) return;
      this.attemptInitializeFromEditorContainer(
        editorContainer,
        generation,
        attempt + 1
      );
    }, GFX_RETRY_DELAY_MS);
  }

  private clearGfxRetry(): void {
    if (this.gfxRetryTimer === null) return;
    clearTimeout(this.gfxRetryTimer);
    this.gfxRetryTimer = null;
  }

  private resolveGfxController(
    editorContainer: EditorContainerLike
  ): GfxController | null {
    const seenStd = new Set<BlockStdLike>();

    const sources: Array<[string, BlockStdLike]> = [];
    const addStd = (label: string, std: BlockStdLike | undefined | null) => {
      if (!std) {
        return;
      }
      if (seenStd.has(std)) {
        return;
      }
      seenStd.add(std);
      sources.push([label, std]);
    };

    addStd('editorContainer.host.std', editorContainer.host?.std);
    addStd('editorContainer.std', editorContainer.std);

    const rootFromHost = editorContainer.host?.querySelector?.(
      'affine-edgeless-root'
    ) as EdgelessRootLike | null | undefined;
    addStd('host.affine-edgeless-root.std', rootFromHost?.std);
    addStd('host.affine-edgeless-root.host.std', rootFromHost?.host?.std);

    const rootFromContainer = editorContainer.querySelector?.(
      'affine-edgeless-root'
    ) as EdgelessRootLike | null | undefined;
    addStd('editorContainer.affine-edgeless-root.std', rootFromContainer?.std);
    addStd(
      'editorContainer.affine-edgeless-root.host.std',
      rootFromContainer?.host?.std
    );

    const rootFromDocument =
      typeof document === 'undefined'
        ? null
        : (document.querySelector(
            'affine-edgeless-root'
          ) as EdgelessRootLike | null);
    addStd('document.affine-edgeless-root.std', rootFromDocument?.std);
    addStd(
      'document.affine-edgeless-root.host.std',
      rootFromDocument?.host?.std
    );

    for (const [label, std] of sources) {
      const gfx = this.resolveGfxFromStd(std, label);
      if (gfx) {
        return gfx;
      }
    }

    return null;
  }

  private resolveGfxFromStd(
    std: BlockStdLike,
    label: string
  ): GfxController | null {
    if (typeof std.getOptional === 'function') {
      try {
        const gfx = std.getOptional(GfxControllerIdentifier) as
          | GfxController
          | null
          | undefined;
        if (gfx) return gfx;
      } catch (error) {
        logger.debug(
          `[ReactiveEdgeService] ${label}.getOptional failed`,
          error
        );
      }
    }

    if (typeof std.get === 'function') {
      try {
        const gfx = std.get(GfxControllerIdentifier) as
          | GfxController
          | null
          | undefined;
        if (gfx) return gfx;
      } catch (error) {
        logger.debug(`[ReactiveEdgeService] ${label}.get failed`, error);
      }
    }

    return null;
  }

  private resolveCanvasContainer(
    editorContainer: EditorContainerLike
  ): Element | null {
    return (
      editorContainer.host?.querySelector?.('.edgeless-container') ??
      editorContainer.querySelector?.('.edgeless-container') ??
      (typeof document === 'undefined'
        ? null
        : (document.querySelector('affine-edgeless-root .edgeless-container') ??
          document.querySelector('.edgeless-container')))
    );
  }

  /**
   * Connects the EdgeSidebarSetup to the EdgeRenderingService.
   * This enables the sidebar callbacks to control edge rendering.
   */
  private connectSidebar(): void {
    const renderingService = this.orchestrator?.getRenderingService();
    if (!renderingService) {
      console.warn(
        '[ReactiveEdgeService] No rendering service available for sidebar'
      );
      return;
    }

    // EdgeRenderingServiceAdapter exposes the internal EdgeRenderingService
    if (renderingService instanceof EdgeRenderingServiceAdapter) {
      const internalService = renderingService.getInternalService();
      this.edgeSidebarSetup.setEdgeRenderingService(internalService);

      // Apply persisted settings (localStorage) on connect
      const { gridStyle, state } = loadEdgeSettings();
      applyCanvasGridStyle(gridStyle);
      internalService.setSystemEnabled('automation', state.automation.enabled);
      internalService.setSystemEnabled('relation', state.relation.enabled);
      internalService.setSystemOpacity(
        'automation',
        state.automation.baseOpacity
      );
      internalService.setSystemOpacity('relation', state.relation.baseOpacity);

      logger.debug(
        '[ReactiveEdgeService] Sidebar connected to EdgeRenderingService'
      );
    }
  }

  /**
   * Disposes the orchestrator.
   */
  private disposeOrchestrator(): void {
    // Disconnect sidebar first
    this.edgeSidebarSetup.clearEdgeRenderingService();

    if (this.orchestrator) {
      this.orchestrator.dispose();
      this.orchestrator = null;
    }
    this.currentGfx = null;
  }

  /**
   * Gets workspace databases for ghost metadata.
   * @param currentDocId - The current document ID, used to determine if a trashed DB is local
   */
  private getWorkspaceDatabases(currentDocId: string): WorkspaceDatabaseInfo[] {
    const activeDatabases =
      this.databaseDiscovery.accessibleDatabases$.value ?? [];
    const trashedDatabases =
      this.databaseDiscovery.accessibleTrashcanDatabases$.value ?? [];
    const writableDatabaseKeys = new Set(
      (this.databaseDiscovery.writableDatabases$.value ?? []).map(
        db => `${db.docId}:${db.blockId}`
      )
    );

    const databasesByKey = new Map<
      string,
      {
        db: (typeof activeDatabases)[number];
        accessState: 'write' | 'read' | 'trashed';
      }
    >();

    for (const db of activeDatabases) {
      databasesByKey.set(`${db.docId}:${db.blockId}`, {
        db,
        accessState: writableDatabaseKeys.has(`${db.docId}:${db.blockId}`)
          ? 'write'
          : 'read',
      });
    }

    for (const db of trashedDatabases) {
      databasesByKey.set(`${db.docId}:${db.blockId}`, {
        db,
        accessState: 'trashed',
      });
    }

    return Array.from(databasesByKey.values()).map(({ db, accessState }) => {
      // Parse originalPosition from xywh ONLY if database is in LOCAL trashcan (same doc)
      // For databases in OTHER docs (even if trashed), we don't use originalPosition
      // because the ghost should be positioned relative to the source element
      const isLocalTrash = db.isInTrash && db.docId === currentDocId;
      const originalPosition = isLocalTrash
        ? parseXywhToPosition(db.xywh)
        : undefined;

      return {
        blockId: db.blockId,
        docId: db.docId,
        docTitle: db.docTitle,
        databaseTitle: db.databaseTitle,
        accessState,
        isInTrash: db.isInTrash,
        originalPosition,
      };
    });
  }

  /**
   * Forces a rescan.
   */
  rescan(): void {
    this.orchestrator?.rescan();
  }

  /**
   * Gets the block registry.
   */
  getBlockRegistry() {
    return this.orchestrator?.getBlockRegistry() ?? null;
  }

  /**
   * Gets the rendering service.
   */
  getRenderingService() {
    return this.orchestrator?.getRenderingService() ?? null;
  }

  /**
   * Whether the service is active.
   */
  isActive(): boolean {
    return this.orchestrator?.isActive() ?? false;
  }

  /**
   * Disposes the service.
   */
  override dispose(): void {
    this.clearGfxRetry();
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.disposeOrchestrator();
    super.dispose();
  }
}
